import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CTASection } from "@/components/sections/CTASection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNotionInsight } from "@/hooks/useNotionInsights";
import { NotionBlockRenderer } from "@/components/notion/NotionBlockRenderer";
import { useLanguage } from "@/contexts/LanguageContext";

const InsightPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: insight, isLoading, error } = useNotionInsight(slug || '');
  const { t, locale } = useLanguage();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <section className="py-16 lg:py-24">
            <div className="section-container">
              <div className="max-w-3xl mx-auto">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-muted rounded w-24" />
                  <div className="h-12 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-1/2" />
                  <div className="h-64 bg-muted rounded mt-8" />
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <section className="py-16 lg:py-24">
            <div className="section-container">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="font-display text-3xl font-bold text-foreground mb-4">
                  {t("insights.notFound")}
                </h1>
                <p className="text-muted-foreground mb-8">
                  {locale === 'es' 
                    ? 'El insight que buscas no existe o ha sido despublicado.'
                    : "The insight you're looking for doesn't exist or has been unpublished."
                  }
                </p>
                <Button variant="hero" asChild>
                  <Link to="/insights">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t("insights.back")}
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const publishedDate = new Date(insight.publishedAt).toLocaleDateString(
    locale === 'es' ? 'es-ES' : 'en-US', 
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20">
        {/* Article Header */}
        <section className="py-12 lg:py-16 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/5 rounded-full blur-[100px]" />
          </div>

          <div className="section-container relative z-10">
            <div className="max-w-3xl mx-auto">
              {/* Back link */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Link
                  to="/insights"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t("insights.back")}
                </Link>
              </motion.div>

              {/* Meta info */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="flex flex-wrap items-center gap-4 mb-6"
              >
                <Badge
                  variant="secondary"
                  className="bg-primary/10 text-primary hover:bg-primary/20"
                >
                  {insight.category}
                </Badge>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  {insight.readTime}
                </span>
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  {publishedDate}
                </span>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6"
              >
                {insight.title}
              </motion.h1>

              {/* Excerpt */}
              {insight.excerpt && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="text-xl text-muted-foreground leading-relaxed"
                >
                  {insight.excerpt}
                </motion.p>
              )}
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-8 lg:py-12">
          <div className="section-container">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              {insight.content && insight.content.length > 0 ? (
                <NotionBlockRenderer blocks={insight.content} />
              ) : (
                <p className="text-muted-foreground text-center py-12">
                  {locale === 'es' 
                    ? 'No hay contenido disponible para este artículo.'
                    : 'No content available for this post.'
                  }
                </p>
              )}
            </motion.article>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default InsightPost;
